import type { Json } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';
import type { StoredFormula } from '@/shared/formula/stored';

/**
 * A value typed by hand over what a formula computes. Ported from TMS
 * `src/lib/cellOverrides.ts`, rescoped to the organization.
 *
 * FOUR states per cell, and the distinction is the whole module:
 *   - `none`  — no row: the formula's result is what shows.
 *   - `value` — a row with a non-null value: that shows instead.
 *   - `blank` — a row whose value is explicitly `null`: the cell is FORCED
 *               empty. Different from `none`, where editing the inputs could
 *               make something appear again; here the user asked for empty and
 *               it stays empty until they undo it.
 *   - `formula` — a versioned JSON envelope containing the formula as STRUCTURE
 *                 (`stored.ts`), keyed by ids rather than names. The result is
 *                 derived at render time and is never persisted.
 *
 * A cell missing from the map is `none`, so callers never have to re-derive the
 * three-way split from a raw row shape.
 *
 * `documentId` splits two scopes that must not mix: `null` is a module's own
 * grid, shared by the organization; a uuid is one Folders spreadsheet, governed
 * by that document's permissions.
 */

// Decoding lives in `@/shared/formula/overrideCodec`, which the Worker shares:
// this module also holds the queries, and the Worker must not pull the browser's
// Supabase client in behind them.
export {
  NO_OVERRIDE,
  decodeOverrideValue,
  formulaEnvelope,
  overrideKey,
  type CellFormulaEnvelope,
  type CellOverrideState,
} from '@/shared/formula/overrideCodec';
import { decodeOverrideValue, formulaEnvelope, overrideKey, type CellOverrideState } from '@/shared/formula/overrideCodec';

/**
 * Every override of one grid. `orgId` is passed explicitly and RLS is what
 * enforces it — the filter here is for the query planner, not for security
 * (CLAUDE.md, invariant 3).
 *
 * A grid renders before its organization resolves, so the id can still be absent:
 * asking anyway sent `org_id=eq.` and PostgREST rejected the empty uuid with a 400
 * on every page load. Nobody's overrides belong to no organization, so the answer
 * is an empty map - the effect re-runs with the id once it arrives.
 */
export async function loadOverrides(
  orgId: string | null | undefined,
  gridKey: string,
  documentId?: string | null,
): Promise<Map<string, CellOverrideState>> {
  if (!orgId) return new Map();
  let query = supabase
    .from('cell_overrides')
    .select('row_key, field, value')
    .eq('org_id', orgId)
    .eq('grid_key', gridKey);

  query = documentId ? query.eq('document_id', documentId) : query.is('document_id', null);

  const { data, error } = await query;

  const map = new Map<string, CellOverrideState>();
  if (error || !data) return map;

  for (const row of data) {
    // `null` is a real stored state, not a missing value: it means blank.
    const state = decodeOverrideValue(row.value);
    if (state) map.set(overrideKey(row.field, row.row_key), state);
  }
  return map;
}

/**
 * All three writes go through one RPC.
 *
 * `.upsert()` cannot be used any more: uniqueness lives in two PARTIAL indexes
 * split on `document_id is null`, and PostgREST's conflict target is a bare
 * column list Postgres will not match to a partial index. The function is
 * `security invoker`, so the policies remain the enforcement — see
 * `20260803001446_grid_upsert_rpcs.sql`.
 */
async function write(
  orgId: string,
  gridKey: string,
  rowKey: string,
  field: string,
  value: Json,
  clear: boolean,
  documentId?: string | null,
): Promise<Error | null> {
  // A defaulted argument is generated as optional rather than nullable, and
  // `exactOptionalPropertyTypes` refuses an explicit `undefined` for one, so
  // the organization-wide scope is named by omitting the argument.
  const { error } = await supabase.rpc('save_cell_override', {
    p_org_id: orgId,
    p_grid_key: gridKey,
    p_row_key: rowKey,
    p_field: field,
    p_value: value,
    p_clear: clear,
    ...(documentId ? { p_document_id: documentId } : {}),
  });
  return error ?? null;
}

/** Sets a manual value. Authorship is recorded by the function itself. */
export function saveOverrideValue(
  orgId: string,
  gridKey: string,
  rowKey: string,
  field: string,
  value: number | string,
  documentId?: string | null,
): Promise<Error | null> {
  return write(orgId, gridKey, rowKey, field, value, false, documentId);
}

/** Stores the formula as structure, without its leading `=` or calculated result. */
export function saveFormulaOverride(
  orgId: string,
  gridKey: string,
  rowKey: string,
  field: string,
  formula: StoredFormula,
  documentId?: string | null,
): Promise<Error | null> {
  return write(orgId, gridKey, rowKey, field, formulaEnvelope(formula) as unknown as Json, false, documentId);
}

/** Forces the cell blank — a row whose value is null, not the absence of a row. */
export function blankOverride(
  orgId: string,
  gridKey: string,
  rowKey: string,
  field: string,
  documentId?: string | null,
): Promise<Error | null> {
  return write(orgId, gridKey, rowKey, field, null, false, documentId);
}

/** Removes the override entirely; the cell reverts to what the formula computes. */
export function clearOverride(
  orgId: string,
  gridKey: string,
  rowKey: string,
  field: string,
  documentId?: string | null,
): Promise<Error | null> {
  return write(orgId, gridKey, rowKey, field, null, true, documentId);
}
